import { flatMap } from 'lodash'
import type { DriverFilter, DriverSchedule, PointsOfInterestResponse } from './types'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { DEFAULT_VIEW_STATE, OFFLINE_THRESHOLD } from './components/DriverMap/constants'
import { LngLatBoundsLike } from 'mapbox-gl'

dayjs.extend(timezone)

export function getActionColor(action: string): string {
  const actionColorMap: Record<string, string> = {
    load: 'var(--theme-color-2)',
    loading: 'var(--theme-color-2)',
    'driving to load': 'var(--theme-color-2)',
    drop: 'var(--theme-color-3)',
    dropping: 'var(--theme-color-3)',
    'driving to drop': 'var(--theme-color-3)',
  }

  return actionColorMap[action] || 'var(--theme-color-1)' // Return mapped color or default
}

export function getTimeDifference(startTime: string, endTime: string): string {
  const start = dayjs(startTime)
  const end = dayjs(endTime)

  const diffMinutes = Math.abs(end.diff(start, 'minutes'))
  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

export function passesSelectedFilters(driver: DriverSchedule, selectedFilters: DriverFilter) {
  let matches = true
  if (selectedFilters.driver_names.length > 0) {
    matches = selectedFilters.driver_names.includes(driver.driver_name)
  }
  if (matches && selectedFilters.depot_names.length > 0) {
    matches = selectedFilters.depot_names.includes(driver.depot_name)
  }
  if (matches && selectedFilters.order_numbers.length > 0) {
    matches = driver.assigned_orders.some((order) => selectedFilters.order_numbers.includes(order.number))
  }
  if (matches && selectedFilters.shift !== 'All') {
    matches = driver.shift === selectedFilters.shift.toLowerCase()
  }
  if (matches && selectedFilters.show_live) {
    matches = !driver.complete && driver.breadcrumbs && driver.breadcrumbs.length > 0
  }
  return matches
}

export function getMapBoundsFromCoordinates(coordinates: { lat: number; lon: number }[]): LngLatBoundsLike | null {
  if (coordinates.length === 0) {
    return null
  }

  // Find min/max lat/lng
  const lats = coordinates.map((c) => c.lat)
  const lngs = coordinates.map((c) => c.lon)

  const north = Math.max(...lats)
  const south = Math.min(...lats)
  const east = Math.max(...lngs)
  const west = Math.min(...lngs)

  return [
    [west, south],
    [east, north],
  ]
}

export function getInitialDriverTrackingBounds(locations: PointsOfInterestResponse): LngLatBoundsLike | null {
  const coordinates = Object.values(locations)
    .flatMap((pois) =>
      pois.map((poi) => ({
        lat: poi.lat,
        lon: poi.lon,
      }))
    )
    .filter((coord) => coord.lat !== null && coord.lon !== null)
  return getMapBoundsFromCoordinates(coordinates)
}

export function calculateOptimalViewState(coordinates: { lat: number; lon: number }[]) {
  // Find the bounds of all coordinates
  const bounds = coordinates.reduce(
    (acc, coord) => {
      return {
        minLat: Math.min(acc.minLat, coord.lat),
        maxLat: Math.max(acc.maxLat, coord.lat),
        minLon: Math.min(acc.minLon, coord.lon),
        maxLon: Math.max(acc.maxLon, coord.lon),
      }
    },
    {
      minLat: Infinity,
      maxLat: -Infinity,
      minLon: Infinity,
      maxLon: -Infinity,
    }
  )

  // Calculate center point
  const centerLat = (bounds.minLat + bounds.maxLat) / 2
  const centerLon = (bounds.minLon + bounds.maxLon) / 2

  // Calculate appropriate zoom level based on the bounds
  // The larger the distance between min/max, the smaller the zoom should be
  const latDiff = bounds.maxLat - bounds.minLat
  const lonDiff = bounds.maxLon - bounds.minLon

  // This is an empirical formula - you may need to adjust based on your needs
  // Larger differences will result in smaller zoom values (more zoomed out)
  const maxDiff = Math.max(latDiff, lonDiff)

  // Base zoom calculation - you can adjust these constants
  let zoom = 14 - Math.log2(maxDiff * 50) // 111km is roughly 1 degree of latitude
  // Clamp zoom to reasonable values
  zoom = Math.max(5, Math.min(zoom, 15))

  return {
    longitude: centerLon,
    latitude: centerLat,
    zoom: zoom,
  }
}

export function getInitialDriverViewState(drivers: DriverSchedule[]) {
  // Extract all coordinates from breadcrumbs (last known position of each driver)
  const coordinates = drivers
    .filter((driver) => driver.last_update?.lat !== null && driver.last_update?.lon !== null)
    .map((driver) => ({ lat: driver.last_update.lat, lon: driver.last_update.lon })) // Filter out any invalid coordinates

  // If no valid coordinates, return default view
  if (coordinates.length === 0) {
    return DEFAULT_VIEW_STATE
  }

  return calculateOptimalViewState(coordinates)
}

export function checkIsDriverOffline(lastHeartbeat: string | null | undefined, complete: boolean): boolean {
  if (!lastHeartbeat) return false // If no heartbeat, do not consider

  const lastUpdate = dayjs(lastHeartbeat)
  const now = dayjs()
  const diffMinutes = now.diff(lastUpdate, 'minute')
  return diffMinutes > OFFLINE_THRESHOLD && !complete // Consider offline if no heartbeat in the last 5 minutes or if the driver is marked as complete
}

export function getFilterOptions(allDriverTracking: DriverSchedule[]): {
  driverOptions: { name: string; disabled: boolean }[]
  depotOptions: string[]
  orderNumberOptions: number[]
} {
  const driverNames: { name: string; disabled: boolean }[] = []
  const depotNames: Set<string> = new Set()
  const orderNumbers: Set<number> = new Set()
  allDriverTracking.forEach((driver) => {
    driverNames.push({ name: driver.driver_name, disabled: checkIsDriverDisabled(driver) })
    depotNames.add(driver.depot_name)
    driver.assigned_orders.forEach((order) => {
      orderNumbers.add(order.number)
    })
  })
  return {
    driverOptions: driverNames.sort((a, b) => a.name.localeCompare(b.name)),
    depotOptions: Array.from(depotNames).sort((a, b) => a.localeCompare(b)),
    orderNumberOptions: Array.from(orderNumbers).sort((a, b) => a - b),
  }
}

export function checkIsDriverDisabled(driverSchedule: DriverSchedule): boolean {
  // A driver is considered disabled if they have no assigned orders
  return !driverSchedule.last_update?.last_heartbeat || !driverSchedule.last_update?.detail
}
